import { callable } from '../callable';

// ─── Section enrollment types ─────────────────────────────────────────────────

export interface EnrollStudentRequest {
  tenantId: string;
  studentId: string;
  sectionId: string;
  /** Entrega 3A: registrar override of an unmet-prerequisite block (audited). */
  prerequisiteOverride?: { reason: string };
  /** Registrar override of an out-of-enrollment-window block (audited). */
  windowOverride?: { reason: string };
}

export interface DropEnrollmentRequest {
  tenantId: string;
  enrollmentId: string;
  reason?: string;
}

export interface RecordGradeRequest {
  tenantId: string;
  enrollmentId: string;
  score: number;
}

export interface CloseGradeRequest {
  tenantId: string;
  enrollmentId: string;
}

// ─── Term enrollment types ────────────────────────────────────────────────────

export interface OpenTermDto {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

export interface FeeConfigDto {
  enrollmentFee: number;
  programTotalFee: number;
  currency: string;
}

export interface OpenTermsForStudentResult {
  terms: OpenTermDto[];
  programName: string | null;
  feeConfig: FeeConfigDto | null;
}

export interface EnrollStudentInTermResult {
  planId: string;
  programName: string;
  termName: string;
  totalAmount: number;
  installmentCount: number;
}

// ─── Portal sections types ────────────────────────────────────────────────────

export interface PortalSectionDto {
  id: string;
  enrollmentId?: string;
  courseId: string;
  courseName: string;
  courseCode: string;
  credits: number;
  facultyId: string;
  facultyName: string;
  maxEnrollment: number;
  currentEnrollment: number;
  /** Program the section belongs to (id) */
  programId?: string;
  /** Program the section belongs to */
  programName?: string;
  /** Prerequisite eligibility for the authenticated student (catalog view).
   *  Defaults to eligible when the program does not enforce prerequisites. */
  eligible?: boolean;
  /** Unmet-prerequisite labels ("CODE — Name") when `eligible` is false. */
  unmetPrereqs?: string[];
  /** Section start date (ISO string), null if not yet defined */
  startDate?: string | null;
  /** Section end date (ISO string), null if not yet defined */
  endDate?: string | null;
  /** Admin override for lifecycle state. Wins over date-based derivation. */
  lifecycleOverride?: 'active' | 'concluded' | null;
  /** Term identifier this section belongs to. */
  termId?: string | null;
  /** Human-readable term name (e.g. "2025-1"). */
  termName?: string | null;
  /** Final letter or numeric grade once the section is graded/closed. */
  finalGrade?: number | null;
  /** La etiqueta del acta, si la hay; el servidor viejo no la manda. */
  finalGradeLabel?: string | null;
  /** Lo que decidió el acta (`enrolled` mientras está en curso). Opcional: el
   *  servidor viejo no lo manda (Hosting despliega antes que las funciones). */
  finalStatus?: 'enrolled' | 'approved' | 'failed';
  /** El estado de la sección; `grades_closed` = su acta está cerrada. */
  status?: string;
  /** El código de la sección. Opcional por el servidor viejo. */
  sectionCode?: string | null;
  /** El horario de la sección (día 0 = domingo). Opcional por el servidor viejo. */
  schedule?: Array<{ dayOfWeek: number; startTime: string; endTime: string; location?: string }>;
}

export interface PortalSectionsResult {
  activeTerm: {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    /** ISO date string (YYYY-MM-DD) — if set, enrollment closes on this date; otherwise term.startDate is the cutoff */
    enrollmentDeadline?: string | null;
    /** Explicit enrollment window (supersedes the legacy deadline) or null. */
    enrollmentWindow?: { opensAt: string; closesAt: string } | null;
    /** Computed: is enrollment open today (window or legacy deadline). */
    enrollmentOpen?: boolean;
  } | null;
  programName: string | null;
  /** Effective charge basis (resolved from feeConfig). Drives the catalog total. */
  chargeBasis?: 'per_year' | 'per_term' | 'per_course' | 'per_credit';
  pricingMode: 'per-credit' | 'fixed';
  pricePerCredit: number;
  /** Flat fee per course (used when chargeBasis = 'per_course'). */
  ratePerCourse?: number;
  programTotalFee: number;
  /** per_year only: the year of the active term is already billed → no new charge. */
  programFeeBilled?: boolean;
  enrollmentFee: number;
  /** Null when the program has no fee configuration: there is no price and no
   *  currency to show. Never a guessed currency. */
  currency: string | null;
  /** The portal shows prices (the program has its fees configured). */
  pricing?: boolean;
  enrolledSections: PortalSectionDto[];
  availableSections: PortalSectionDto[];
}

export interface EnrollStudentInSectionsResult {
  /**
   * When true, this is a pay_to_enroll program: the enrollment is NOT yet
   * confirmed — the student must complete the online checkout. The webhook
   * materializes the enrollment on payment approval. `planId`/`enrollmentIds`
   * are absent in this case.
   */
  requiresPayment?: boolean;
  /** Hosted checkout URL to redirect the buyer to (pay_to_enroll only). */
  checkoutUrl?: string | null;
  /** EnrollmentIntent id created for the checkout (pay_to_enroll only). */
  intentId?: string;
  planId?: string;
  programName: string;
  termName?: string;
  totalAmount: number;
  currency: string | null;
  enrollmentIds?: string[];
  /** Pricing mode of the program at enrollment time. */
  pricingMode: 'fixed' | 'per-credit';
  /** Price per credit at enrollment time (0 for fixed plans). */
  pricePerCredit: number;
  /** Total credits of the sections enrolled in this call. Used to trigger the credit adjustment dialog. */
  additionalCredits: number;
}

// ─── Enrollment API ───────────────────────────────────────────────────────────

export const enrollmentApi = {
  // Section-level enrollment (admin)
  enrollStudent: async (data: EnrollStudentRequest) => {
    // `noCharge: 'no_program'`: inscrito SIN cobro porque la persona no tiene
    // programa, con Finanzas encendido (H4). La pantalla lo avisa.
    const fn = callable<EnrollStudentRequest, { id: string; noCharge?: 'no_program' }>(
      'enrollStudent',
    );
    const result = await fn(data);
    return result.data;
  },

  /** H8: antes del inicio de la sección la inscripción se borra (`removed`);
   *  desde el inicio queda W (`withdrawn`, solo el personal). */
  dropEnrollment: async (data: DropEnrollmentRequest) => {
    const fn = callable<
      DropEnrollmentRequest,
      { id: string; mode?: 'removed' | 'withdrawn'; billing?: 'recompute' | 'keep' }
    >('dropEnrollment');
    const result = await fn(data);
    return result.data;
  },

  /** H8: el personal cambia a un alumno de sección (mismo curso y período). */
  changeEnrollmentSection: async (data: {
    tenantId: string;
    enrollmentId: string;
    toSectionId: string;
    reason?: string;
  }) => {
    const fn = callable<typeof data, { id: string }>('changeEnrollmentSection');
    const result = await fn(data);
    return result.data;
  },

  recordGrade: async (data: RecordGradeRequest) => {
    const fn = callable<RecordGradeRequest, { gradeId: string }>('recordGrade');
    const result = await fn(data);
    return result.data;
  },

  closeGrade: async (data: CloseGradeRequest) => {
    const fn = callable<CloseGradeRequest, void>('closeGrade');
    await fn(data);
  },

  // Term-level self-enrollment (student portal)
  getOpenTermsForStudent: async (params: {
    tenantId: string;
  }): Promise<OpenTermsForStudentResult> => {
    const fn = callable<typeof params, OpenTermsForStudentResult>('getOpenTermsForStudent');
    const result = await fn(params);
    return result.data;
  },

  enrollStudentInTerm: async (params: {
    tenantId: string;
    termId: string;
    paymentPlanOption: string;
  }): Promise<EnrollStudentInTermResult> => {
    const fn = callable<typeof params, EnrollStudentInTermResult>('enrollStudentInTerm');
    const result = await fn(params);
    return result.data;
  },

  // Student portal wizard enrollment
  // El alumno es el de la sesión (o el de la vista «Ver portal»): lo resuelve el servidor.
  getSectionsForPortal: async (params: { tenantId: string }): Promise<PortalSectionsResult> => {
    const fn = callable<typeof params, PortalSectionsResult>('getSectionsForPortal');
    const result = await fn(params);
    return result.data;
  },

  enrollStudentInSections: async (params: {
    tenantId: string;
    termId: string;
    sectionIds: string[];
    paymentPlanOption: string;
    /** Caller origin, so pay_to_enroll checkout returns to the right domain. */
    returnOrigin?: string;
  }): Promise<EnrollStudentInSectionsResult> => {
    const fn = callable<typeof params, EnrollStudentInSectionsResult>('enrollStudentInSections');
    const result = await fn(params);
    return result.data;
  },
};
